import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CheckCircleIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, SortableTableHeader, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { useAuthContext } from '../../contexts/AuthContext'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { estoqueService } from '../../services/estoque'
import { marketingNotasService } from '../../services/marketingNotas'
import type { EstoqueFornecedor } from '../../types/estoque'
import type {
  MarketingCampaign,
  MarketingEvent,
  MarketingInvoice,
  MarketingInvoiceAllocationInput,
  MarketingInvoiceDocumentType,
  MarketingInvoiceFilters,
  MarketingInvoiceInput,
  MarketingInvoiceStatus,
} from '../../types/marketingNotas'
import { paginateRows, sortRows } from '../Estoque/estoqueUi'
import EstoquePagination from '../Estoque/EstoquePagination'
import NotasConfirmEntradaModal from './NotasConfirmEntradaModal'
import NotasFormModal from './NotasFormModal'
import {
  ConfirmCancelledError,
  ICON_BTN_CLASS,
  ICON_BTN_DANGER_CLASS,
  MODULE_NAME,
  PAGE_SIZE,
  PERM_LOADING,
  SELECT_CLASS,
  documentTypeBadgeClass,
  documentTypeLabel,
  formatCurrency,
  formatDateOnlyPtBr,
  invoiceTotal,
  paymentsLogSummary,
  statusBadgeClass,
  statusLabel,
} from './notasUi'
import { formatRequestNumber } from './solicitacaoUi'

const Notas: React.FC = () => {
  const [searchParams] = useSearchParams()
  const { user } = useAuthContext()
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { logCreate, logUpdate, logDelete, logView } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('Notas fiscais')
  const [moduleDescription, setModuleDescription] = useState(
    'Cadastro de NF-e e NFS-e de marketing'
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invoices, setInvoices] = useState<MarketingInvoice[]>([])
  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([])
  const [events, setEvents] = useState<MarketingEvent[]>([])

  const [filters, setFilters] = useState<MarketingInvoiceFilters>(() => ({
    documentType: '',
    status: '',
    number: searchParams.get('q')?.trim() || '',
    supplierId: '',
    dateFrom: '',
    dateTo: '',
    requestNumber: '',
  }))

  const [sortKey, setSortKey] = useState('issue_date')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<MarketingInvoice | null>(null)
  const [saving, setSaving] = useState(false)

  const [confirmInvoice, setConfirmInvoice] = useState<MarketingInvoice | null>(null)
  const [confirmBusy, setConfirmBusy] = useState(false)

  const [viewInvoice, setViewInvoice] = useState<MarketingInvoice | null>(null)

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [invoicesData, fornecedoresData, campaignsData, eventsData] = await Promise.all([
        marketingNotasService.listInvoices(filters),
        estoqueService.listFornecedores(true),
        marketingNotasService.listCampaigns(true),
        marketingNotasService.listEvents(true),
      ])
      setInvoices(invoicesData)
      setFornecedores(fornecedoresData)
      setCampaigns(campaignsData)
      setEvents(eventsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar notas fiscais.')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  useEffect(() => {
    void logView('marketing_invoice', null, 'Visualizou tela de notas fiscais de marketing')
  }, [logView])

  const sorted = useMemo(
    () =>
      sortRows(invoices, sortKey, sortOrder, (row, key) => {
        if (key === 'issue_date') return row.issue_date
        if (key === 'number') return row.number
        if (key === 'supplier') return row.supplier?.name || ''
        if (key === 'payment_source') return row.payment_source?.name || ''
        if (key === 'type') return row.document_type
        if (key === 'status') return row.status
        if (key === 'total') return invoiceTotal(row)
        return ''
      }),
    [invoices, sortKey, sortOrder]
  )

  const pageRows = paginateRows(sorted, page, PAGE_SIZE)

  const toggleSort = (key: string) => {
    if (sortKey === key) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortOrder(key === 'issue_date' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }

  const openEdit = (invoice: MarketingInvoice) => {
    if (invoice.status !== 'draft') return
    setEditing(invoice)
    setFormOpen(true)
  }

  const invoiceLogSnapshot = (invoice: MarketingInvoice) => ({
    number: invoice.number,
    series: invoice.series,
    document_type: invoice.document_type,
    status: invoice.status,
    supplier_id: invoice.supplier_id,
    supplier_name: invoice.supplier?.name ?? null,
    payment_source_id: invoice.payment_source_id,
    payment_source_name: invoice.payment_source?.name ?? null,
    issue_date: invoice.issue_date,
    freight_value: invoice.freight_value,
    discount_value: invoice.discount_value,
    request_id: invoice.request_id,
    supplier_quote_id: invoice.supplier_quote_id,
    campaign_id: invoice.campaign_id,
    event_id: invoice.event_id,
    items_count: invoice.items?.length || 0,
    total: invoiceTotal(invoice),
    ...paymentsLogSummary(invoice.payments),
  })

  const handleSave = async (input: MarketingInvoiceInput): Promise<MarketingInvoice> => {
    setSaving(true)
    setError(null)
    try {
      let saved: MarketingInvoice
      if (editing) {
        saved = await marketingNotasService.updateInvoice(editing.id, input)
        void logUpdate(
          'marketing_invoice',
          saved.id,
          `Atualizou rascunho de nota ${documentTypeLabel(saved.document_type)} ${saved.number}`,
          invoiceLogSnapshot(editing),
          invoiceLogSnapshot(saved)
        )
      } else {
        saved = await marketingNotasService.createInvoice(input, user?.id)
        void logCreate(
          'marketing_invoice',
          saved.id,
          `Criou rascunho de nota ${documentTypeLabel(saved.document_type)} ${saved.number}`,
          invoiceLogSnapshot(saved)
        )
      }
      setEditing(saved)
      await loadAll()
      return saved
    } catch (err) {
      throw err
    } finally {
      setSaving(false)
    }
  }

  const confirmNfse = async (
    invoice: MarketingInvoice,
    options?: { throwOnCancel?: boolean }
  ) => {
    if (!window.confirm(`Confirmar a NFS-e ${invoice.number}?`)) {
      if (options?.throwOnCancel) throw new ConfirmCancelledError()
      return
    }
    setConfirmBusy(true)
    setError(null)
    try {
      const confirmed = await marketingNotasService.confirmInvoice(invoice.id, [])
      void logUpdate(
        'marketing_invoice',
        confirmed.id,
        `Confirmou NFS-e ${confirmed.number}`,
        { status: invoice.status, confirmed_at: invoice.confirmed_at },
        { status: confirmed.status, confirmed_at: confirmed.confirmed_at }
      )
      await loadAll()
    } catch (err) {
      if (options?.throwOnCancel) throw err
      setError(err instanceof Error ? err.message : 'Erro ao confirmar a nota.')
    } finally {
      setConfirmBusy(false)
    }
  }

  const handleConcludeFromModal = async (invoice: MarketingInvoice) => {
    if (invoice.status !== 'draft') {
      throw new Error('Somente rascunhos podem ser confirmados.')
    }
    if (invoice.document_type === 'nfe') {
      setConfirmInvoice(invoice)
      return
    }
    await confirmNfse(invoice, { throwOnCancel: true })
  }

  const handleDelete = async (invoice: MarketingInvoice) => {
    if (invoice.status !== 'draft') return
    if (!window.confirm(`Excluir o rascunho da nota ${invoice.number}?`)) return
    try {
      await marketingNotasService.softDeleteInvoice(invoice.id, {
        deleted_by: user?.id,
        deleted_reason: 'Exclusão de rascunho pelo usuário',
      })
      void logDelete(
        'marketing_invoice',
        invoice.id,
        `Excluiu rascunho de nota ${documentTypeLabel(invoice.document_type)} ${invoice.number}`,
        invoiceLogSnapshot(invoice)
      )
      await loadAll()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir a nota.')
    }
  }

  const openView = (invoice: MarketingInvoice) => {
    setViewInvoice(invoice)
    void logView(
      'marketing_invoice',
      invoice.id,
      `Visualizou nota ${documentTypeLabel(invoice.document_type)} ${invoice.number}`
    )
  }

  const startConfirm = (invoice: MarketingInvoice) => {
    if (invoice.status !== 'draft') return
    if (invoice.document_type === 'nfse') {
      void confirmNfse(invoice)
      return
    }
    setConfirmInvoice(invoice)
  }

  const handleConfirmNfe = async (allocations: MarketingInvoiceAllocationInput[]) => {
    if (!confirmInvoice) return
    setConfirmBusy(true)
    setError(null)
    try {
      const confirmed = await marketingNotasService.confirmInvoice(confirmInvoice.id, allocations)
      void logUpdate(
        'marketing_invoice',
        confirmed.id,
        `Confirmou entrada de NF-e ${confirmed.number}`,
        { status: confirmInvoice.status, confirmed_at: confirmInvoice.confirmed_at },
        {
          status: confirmed.status,
          confirmed_at: confirmed.confirmed_at,
          allocations_count: allocations.length,
          allocations,
        }
      )

      const movementIds = (confirmed.items || []).flatMap((item) =>
        (item.allocations || [])
          .map((allocation) => allocation.movement_id)
          .filter((id): id is string => Boolean(id))
      )
      if (movementIds.length > 0) {
        void logCreate(
          'marketing_stock_movement',
          movementIds[0],
          `Registrou entradas de estoque pela NF-e ${confirmed.number}`,
          {
            invoice_id: confirmed.id,
            invoice_number: confirmed.number,
            movement_ids: movementIds,
            allocations_count: allocations.length,
          }
        )
      }

      setConfirmInvoice(null)
      await loadAll()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao confirmar entrada no estoque.')
    } finally {
      setConfirmBusy(false)
    }
  }

  return (
    <div className="flex h-full flex-1 flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        loadingComponent={PERM_LOADING}
        fallback={
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <Heading as="h2" size="xl" className="text-gray-900 dark:text-white">
                Acesso negado
              </Heading>
              <Text color="muted" className="mt-2">
                Você não tem permissão para visualizar notas fiscais de marketing.
              </Text>
            </div>
          </div>
        }
      >
        <div className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <Heading as="h1" size="2xl" className="text-gray-900 dark:text-white">
                {moduleTitle}
              </Heading>
              <Text color="muted" className="mt-1">
                {moduleDescription}
              </Text>
            </div>
            {canInsert && (
              <PermissionGuard moduleName={MODULE_NAME} permissionType="insert">
                <Button
                  type="button"
                  variant="solid"
                  onClick={openCreate}
                  className="inline-flex items-center gap-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  Nova nota
                </Button>
              </PermissionGuard>
            )}
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mb-4 grid grid-cols-1 gap-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Tipo
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={filters.documentType || ''}
                onChange={(e) => {
                  setFilters((f) => ({
                    ...f,
                    documentType: e.target.value as MarketingInvoiceDocumentType | '',
                  }))
                  setPage(1)
                }}
              >
                <option value="">Todos</option>
                <option value="nfe">NF-e</option>
                <option value="nfse">NFS-e</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Status
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={filters.status || ''}
                onChange={(e) => {
                  setFilters((f) => ({
                    ...f,
                    status: e.target.value as MarketingInvoiceStatus | '',
                  }))
                  setPage(1)
                }}
              >
                <option value="">Todos</option>
                <option value="draft">Rascunho</option>
                <option value="confirmed">Confirmada</option>
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Número
              </label>
              <Input
                className="w-full"
                value={filters.number || ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, number: e.target.value }))
                  setPage(1)
                }}
                placeholder="Buscar número"
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Solicitação
              </label>
              <Input
                className="w-full"
                value={filters.requestNumber || ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, requestNumber: e.target.value }))
                  setPage(1)
                }}
                placeholder="#123"
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Fornecedor
              </label>
              <select
                className={SELECT_CLASS + ' w-full'}
                value={filters.supplierId || ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, supplierId: e.target.value }))
                  setPage(1)
                }}
              >
                <option value="">Todos</option>
                {fornecedores.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                De
              </label>
              <Input
                type="date"
                className="w-full min-w-0"
                value={filters.dateFrom || ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, dateFrom: e.target.value }))
                  setPage(1)
                }}
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Até
              </label>
              <Input
                type="date"
                className="w-full min-w-0"
                value={filters.dateTo || ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, dateTo: e.target.value }))
                  setPage(1)
                }}
              />
            </div>
          </div>

          {loading && invoices.length === 0 ? (
            <div className="flex justify-center py-20">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <SortableTableHeader
                        sortKey="issue_date"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Emissão
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="number"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Número
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="type"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Tipo
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="supplier"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Fornecedor
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="payment_source"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Fonte Pagadora
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="status"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Status
                      </SortableTableHeader>
                      <SortableTableHeader
                        sortKey="total"
                        currentSort={sortKey}
                        sortOrder={sortOrder}
                        onSort={toggleSort}
                        className="px-4 py-3"
                      >
                        Total
                      </SortableTableHeader>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Vínculos
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {pageRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400"
                        >
                          Nenhuma nota fiscal encontrada.
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                            {formatDateOnlyPtBr(invoice.issue_date)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {invoice.number}
                            {invoice.series ? (
                              <span className="ml-1 text-xs text-gray-500">
                                / {invoice.series}
                              </span>
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${documentTypeBadgeClass(invoice.document_type)}`}
                            >
                              {documentTypeLabel(invoice.document_type)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                            {invoice.supplier?.name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                            {invoice.payment_source?.name || '—'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(invoice.status)}`}
                            >
                              {statusLabel(invoice.status)}
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                            {formatCurrency(invoiceTotal(invoice))}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                            {[
                              invoice.request
                                ? formatRequestNumber(invoice.request.request_number)
                                : null,
                              invoice.campaign?.name,
                              invoice.event?.name,
                            ]
                              .filter(Boolean)
                              .join(' · ') || '—'}
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-right">
                            <div className="inline-flex items-center gap-1">
                              <button
                                type="button"
                                title="Detalhes"
                                className={ICON_BTN_CLASS}
                                onClick={() => openView(invoice)}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              {invoice.status === 'draft' && canUpdate && (
                                <>
                                  <PermissionGuard
                                    moduleName={MODULE_NAME}
                                    permissionType="update"
                                  >
                                    <button
                                      type="button"
                                      title="Editar"
                                      className={ICON_BTN_CLASS}
                                      onClick={() => openEdit(invoice)}
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      type="button"
                                      title="Confirmar entrada"
                                      className={ICON_BTN_CLASS}
                                      onClick={() => startConfirm(invoice)}
                                      disabled={confirmBusy}
                                    >
                                      <CheckCircleIcon className="h-4 w-4" />
                                    </button>
                                  </PermissionGuard>
                                </>
                              )}
                              {invoice.status === 'draft' && canDelete && (
                                <PermissionGuard moduleName={MODULE_NAME} permissionType="delete">
                                  <button
                                    type="button"
                                    title="Excluir rascunho"
                                    className={ICON_BTN_DANGER_CLASS}
                                    onClick={() => void handleDelete(invoice)}
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </PermissionGuard>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <EstoquePagination
                page={page}
                pageSize={PAGE_SIZE}
                total={sorted.length}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>

        <NotasFormModal
          open={formOpen}
          editing={editing}
          campaigns={campaigns}
          events={events}
          busy={saving || confirmBusy}
          onClose={() => {
            if (!saving && !confirmBusy) {
              setFormOpen(false)
              setEditing(null)
            }
          }}
          onSave={handleSave}
          onConclude={handleConcludeFromModal}
        />

        <NotasConfirmEntradaModal
          open={Boolean(confirmInvoice)}
          invoice={confirmInvoice}
          busy={confirmBusy}
          onClose={() => {
            if (!confirmBusy) setConfirmInvoice(null)
          }}
          onConfirm={handleConfirmNfe}
        />

        {viewInvoice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setViewInvoice(null)} />
            <div className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
                <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                  {documentTypeLabel(viewInvoice.document_type)} {viewInvoice.number}
                </Heading>
                <Button type="button" variant="outline" size="sm" onClick={() => setViewInvoice(null)}>
                  Fechar
                </Button>
              </div>
              <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Text size="xs" color="muted">
                      Status
                    </Text>
                    <Text>{statusLabel(viewInvoice.status)}</Text>
                  </div>
                  <div>
                    <Text size="xs" color="muted">
                      Emissão
                    </Text>
                    <Text>{formatDateOnlyPtBr(viewInvoice.issue_date)}</Text>
                  </div>
                  <div>
                    <Text size="xs" color="muted">
                      Fornecedor
                    </Text>
                    <Text>{viewInvoice.supplier?.name || '—'}</Text>
                  </div>
                  <div>
                    <Text size="xs" color="muted">
                      Fonte Pagadora
                    </Text>
                    <Text>{viewInvoice.payment_source?.name || '—'}</Text>
                  </div>
                  {viewInvoice.document_type === 'nfe' && (
                    <div>
                      <Text size="xs" color="muted">
                        Frete
                      </Text>
                      <Text>{formatCurrency(viewInvoice.freight_value || 0)}</Text>
                    </div>
                  )}
                  <div>
                    <Text size="xs" color="muted">
                      Desconto
                    </Text>
                    <Text>{formatCurrency(viewInvoice.discount_value || 0)}</Text>
                  </div>
                  <div>
                    <Text size="xs" color="muted">
                      Solicitação
                    </Text>
                    <Text>
                      {viewInvoice.request
                        ? formatRequestNumber(viewInvoice.request.request_number)
                        : '—'}
                    </Text>
                  </div>
                  <div>
                    <Text size="xs" color="muted">
                      Orçamento
                    </Text>
                    <Text>
                      {viewInvoice.supplier_quote
                        ? `#${String(viewInvoice.supplier_quote.quote_number).padStart(4, '0')} · ${viewInvoice.supplier_quote.title}`
                        : '—'}
                    </Text>
                  </div>
                  <div>
                    <Text size="xs" color="muted">
                      Campanha / Evento
                    </Text>
                    <Text>
                      {[viewInvoice.campaign?.name, viewInvoice.event?.name]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </Text>
                  </div>
                </div>
                <div>
                  <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Itens
                  </Text>
                  <ul className="space-y-2">
                    {(viewInvoice.items || []).map((item) => (
                      <li
                        key={item.id}
                        className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {item.stock_item
                            ? item.stock_item.name
                            : item.description || '—'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          Qtd {item.quantity} × {formatCurrency(item.unit_value)}
                          {Number(item.discount_value || 0) > 0
                            ? ` − desc. ${formatCurrency(item.discount_value)}`
                            : ''}{' '}
                          = {formatCurrency(item.total_value)}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 text-right font-semibold text-gray-900 dark:text-white">
                    Total: {formatCurrency(invoiceTotal(viewInvoice))}
                  </div>
                </div>

                <div>
                  <Text size="sm" className="mb-2 font-semibold text-gray-900 dark:text-white">
                    Formas de pagamento
                  </Text>
                  {(viewInvoice.payments || []).length === 0 ? (
                    <Text size="sm" color="muted">
                      Nenhum pagamento informado.
                    </Text>
                  ) : (
                    <ul className="space-y-2">
                      {(viewInvoice.payments || []).map((payment) => (
                        <li
                          key={payment.id}
                          className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700"
                        >
                          <div className="font-medium text-gray-900 dark:text-white">
                            {payment.payment_method?.name || 'Forma'} · parcela{' '}
                            {payment.installment_number}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatCurrency(payment.amount)} · venc.{' '}
                            {formatDateOnlyPtBr(payment.due_date)}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </PermissionGuard>
    </div>
  )
}

export default Notas
